import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { ButtonGroup, Spinner, Alert, Form } from 'react-bootstrap';
import { FaPlay, FaPause } from 'react-icons/fa';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { initializeMapbox, getMapStyle } from '../../map/config/mapConfig';
import { useDeerSightingsHeatmap, HEATMAP_PRESETS } from '../hooks/useDeerSightingsHeatmap';
import { formatLocalDate } from '../../../utils/dateUtils';
import './DeerSightingsHeatmap.css';

const SOURCE_ID = 'deer-sightings-points';
const LAYER_ID = 'deer-sightings-circles';

/** Static backdrop: no pan/zoom/scroll interaction. */
export const HEATMAP_MAP_OPTIONS = {
  center: [-86.9, 33.5],
  zoom: 6,
  interactive: false,
  attributionControl: true,
};

/** Full extent of heat points with a small pad — used once for the static camera. */
export function pointsToBounds(points) {
  if (!points.length) return null;
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  points.forEach((p) => {
    if (p.lng < minLng) minLng = p.lng;
    if (p.lat < minLat) minLat = p.lat;
    if (p.lng > maxLng) maxLng = p.lng;
    if (p.lat > maxLat) maxLat = p.lat;
  });
  if (minLng === Infinity) return null;
  const pad = 0.002;
  return [
    [minLng - pad, minLat - pad],
    [maxLng + pad, maxLat + pad],
  ];
}

function buildGeoJSONFromPoints(points) {
  const features = points.map((p) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
    properties: { deer_seen: p.deer_seen },
  }));
  return { type: 'FeatureCollection', features };
}

/**
 * Deer sightings heatmap widget. Reusable for club or personal.
 * Camera is static: fit once to the full extent of loaded points; no pan/zoom.
 * @param {'club'|'personal'} mode
 * @param {{ clubId?: number }} options - clubId for club mode
 */
export function DeerSightingsHeatmap({ mode, clubId }) {
  const {
    points,
    loading,
    error,
    presetId,
    setPresetId,
  } = useDeerSightingsHeatmap(mode, { clubId });

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const playIntervalRef = useRef(null);

  const sortedDates = useMemo(() => {
    if (!points.length) return [];
    const set = new Set(points.map((p) => p.date));
    return Array.from(set).sort();
  }, [points]);

  const [sliderIndex, setSliderIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const currentDate = sortedDates[sliderIndex] ?? sortedDates[sortedDates.length - 1] ?? null;

  const visiblePoints = useMemo(() => {
    if (!currentDate) return [];
    return points.filter((p) => p.date <= currentDate);
  }, [points, currentDate]);

  const maxDeer = useMemo(() => {
    if (!visiblePoints.length) return 1;
    return Math.max(1, ...visiblePoints.map((p) => p.deer_seen));
  }, [visiblePoints]);

  useEffect(() => {
    setSliderIndex(sortedDates.length > 0 ? sortedDates.length - 1 : 0);
  }, [sortedDates.length]);

  useEffect(() => {
    initializeMapbox();
  }, []);

  useEffect(() => {
    return () => {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
        playIntervalRef.current = null;
      }
    };
  }, []);

  // Create a non-interactive map only after loading finishes so the container is in the DOM
  useEffect(() => {
    if (loading || !mapContainerRef.current || !getMapStyle || (typeof process !== 'undefined' && process.env.NODE_ENV === 'test')) return;
    const style = getMapStyle('satellite');
    if (!mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style,
        ...HEATMAP_MAP_OPTIONS,
      });
    }
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [loading]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const ensureLayer = () => {
      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
          type: 'geojson',
          data: buildGeoJSONFromPoints(visiblePoints),
        });
        map.addLayer({
          id: LAYER_ID,
          type: 'circle',
          source: SOURCE_ID,
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['get', 'deer_seen'], 0, 8, Math.max(1, maxDeer), 48],
            'circle-color': 'rgba(255, 140, 0, 0.6)',
            'circle-blur': 0.4,
            'circle-opacity': ['interpolate', ['linear'], ['get', 'deer_seen'], 0, 0.3, Math.max(1, maxDeer), 0.85],
          },
        });
      } else {
        const source = map.getSource(SOURCE_ID);
        if (source) source.setData(buildGeoJSONFromPoints(visiblePoints));
      }
    };
    if (map.isStyleLoaded && map.isStyleLoaded()) {
      ensureLayer();
    } else {
      map.once('load', ensureLayer);
      return () => map.off('load', ensureLayer);
    }
  }, [visiblePoints, maxDeer]);

  // Fit once to the full extent of loaded points; do not refit as the timeline reveals points
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.fitBounds || points.length === 0) return;
    const b = pointsToBounds(points);
    if (b) map.fitBounds(b, { padding: 60, maxZoom: 15, duration: 0 });
  }, [points]);

  const handlePlay = useCallback(() => {
    if (isPlaying && playIntervalRef.current) {
      clearInterval(playIntervalRef.current);
      playIntervalRef.current = null;
      setIsPlaying(false);
      return;
    }
    if (sortedDates.length === 0) return;
    if (sliderIndex >= sortedDates.length - 1) setSliderIndex(0);
    setIsPlaying(true);
    playIntervalRef.current = setInterval(() => {
      setSliderIndex((i) => {
        if (i >= sortedDates.length - 1) {
          if (playIntervalRef.current) clearInterval(playIntervalRef.current);
          playIntervalRef.current = null;
          setIsPlaying(false);
          return i;
        }
        return i + 1;
      });
    }, 400);
  }, [isPlaying, sliderIndex, sortedDates.length]);

  if (error) {
    return (
      <div className="deer-sightings-heatmap">
        <Alert variant="warning">{error}</Alert>
      </div>
    );
  }

  return (
    <div className="deer-sightings-heatmap">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <span className="text-muted small me-1">Range:</span>
        <ButtonGroup size="sm">
          {HEATMAP_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`btn ${presetId === p.id ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setPresetId(p.id)}
            >
              {p.label}
            </button>
          ))}
        </ButtonGroup>
      </div>

      {loading ? (
        <div className="deer-sightings-heatmap-loading">
          <Spinner animation="border" size="sm" />
          <span className="ms-2">Loading heatmap...</span>
        </div>
      ) : (
        <>
          <div ref={mapContainerRef} className="deer-sightings-heatmap-map" />
          {sortedDates.length > 0 && (
            <div className="deer-sightings-heatmap-timeline mt-2">
              <div className="d-flex align-items-center gap-2">
                <Form.Range
                  min={0}
                  max={Math.max(0, sortedDates.length - 1)}
                  step={1}
                  value={sliderIndex}
                  onChange={(e) => setSliderIndex(Number(e.target.value))}
                  className="flex-grow-1"
                />
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={handlePlay}
                  title={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? <FaPause /> : <FaPlay />}
                </button>
              </div>
              <div className="small text-muted mt-1">
                {currentDate ? formatLocalDate(currentDate, { month: 'short', day: 'numeric', year: 'numeric' }, 'en-US') : '—'}
                {' · '}
                Cumulative: {visiblePoints.length} location{visiblePoints.length !== 1 ? 's' : ''}
              </div>
            </div>
          )}
          {!loading && points.length === 0 && (
            <p className="text-muted small mt-2 mb-0">No location data for this range. Log hunts with a club location or map stand to see the heatmap.</p>
          )}
        </>
      )}
    </div>
  );
}
